import React from 'react';
import { Box } from '@mui/material';
import { RADIUS } from '../../theme/tokens';

/**
 * The one card shell for every list row — products, areas, and (later) customers, bills, dues.
 *
 * Before this, each list hand-rolled its own card: different radii, different padding, some
 * outlined and some elevated, some with a colored status rail. Two lists on the same screen
 * looked like two different apps. Everything visual lives here now so they can't drift again.
 *
 * Inactive rows are shown with `muted` — a sunken background — not a colored rail and not
 * opacity. A rail spends the strongest signal on screen, a saturated edge, on something a chip
 * in the row already says, and its green competes with every other green in the card. Opacity
 * is worse: it creates a stacking context, so it would fade the row's buttons too and make
 * still-usable actions read as disabled. Fade the identity text inside the card if you want
 * that, where it can be scoped.
 *
 * @param muted    sunken background — an inactive/paused row that must stay fully usable
 * @param dashed   dashed border — a placeholder bucket rather than a real record
 * @param active   primary border — expanded, selected, or being dragged
 * @param leading  fixed-width slot before the content, flush to the card edge (drag grip)
 * @param onClick  makes the whole card tappable
 * @param sx       merged last, for per-list spacing only — not for color or radius
 *
 * Forwards its ref to the outer element — dnd-kit's `setNodeRef` needs the real DOM node.
 */
const ListCard = React.forwardRef(function ListCard({
    muted = false, dashed = false, active = false,
    leading = null, onClick, children, sx = {}, ...props
}, ref) {
    return (
        <Box
            ref={ref}
            onClick={onClick}
            sx={{
                display: 'flex', alignItems: 'stretch',
                border: dashed ? '1px dashed' : '1px solid',
                borderColor: active ? 'primary.main' : 'divider',
                borderRadius: `${RADIUS.md}px`,
                bgcolor: muted ? 'action.hover' : 'background.paper',
                overflow: 'hidden',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'border-color 0.2s, background-color 0.2s',
                ...sx,
            }}
            {...props}
        >
            {leading}
            {/* minWidth:0 so long names can ellipsize instead of stretching the card */}
            <Box sx={{ flex: 1, minWidth: 0, py: 1.25, px: 1.5 }}>
                {children}
            </Box>
        </Box>
    );
});

export default ListCard;
